<!-- Header Section — kept outside the loading branch so filtering doesn't drop focus -->
<div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
  <div>
    <h5 class="mb-1 fw-bold">Raw Materials</h5>
    <span class="sub-text">Manage material types and specifications</span>
  </div>

  <div class="d-flex flex-wrap gap-2">
    <button
      class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
      (click)="exportRawMaterials()"
      [disabled]="isExporting">
      @if (isExporting) {
        <span class="spinner-border spinner-border-sm"></span>
      } @else {
        <i class="bi bi-download"></i>
      }
      Export
    </button>

    <button class="btn btn-primary btn-enjochem" (click)="openCreateModal()">
      <i class="bi bi-plus-circle"></i>
      <span>Add Raw Material</span>
    </button>
  </div>
</div>

<div class="row g-2 mb-4">
  <div class="col-12 col-md-4">
    <div class="input-group">
      <span class="input-group-text bg-white border-end-0">
        <i class="bi bi-search text-muted"></i>
      </span>
      <input
        type="text"
        class="form-control border-start-0 ps-0"
        placeholder="Search by name, code or HSN"
        [ngModel]="searchTerm"
        (ngModelChange)="onSearchChange($event)" />
    </div>
  </div>

  <div class="col-12 col-md-4">
    <ng-select
      placeholder="All Companies"
      [items]="companies"
      [(ngModel)]="selectedCompanyId"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="true"
      (ngModelChange)="onFilterChange()" />
  </div>

  <div class="col-12 col-md-4">
    <ng-select
      placeholder="All Categories"
      [items]="rawMaterialCategories"
      [(ngModel)]="selectedCategoryId"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="false"
      (ngModelChange)="onFilterChange()" />
  </div>
</div>

<app-error-alert [error]="pageError"></app-error-alert>
<app-error-alert [error]="deleteError"></app-error-alert>

@if (isRawMaterialDataLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  <div class="table-responsive border bg-white">
    @if ((rawMaterials || []).length === 0) {
      <div class="empty-state-container">
        <i class="bi bi-box-seam"></i>
        <h5>No Raw Materials Found</h5>
        @if (hasActiveFilters) {
          <p>No materials match the current search or filters.</p>
        } @else {
          <p>
            You can add material by clicking the
            <strong>+ Add Raw Material</strong>
            button.
          </p>
        }
      </div>
    } @else {
      <table class="table align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>
              <button
                type="button"
                class="btn btn-link p-0 border-0 text-muted text-decoration-none d-inline-flex align-items-center gap-1"
                (click)="onToggleCodeSort()"
                [title]="sortDirection === 'asc' ? 'Sorted low to high' : 'Sorted high to low'">
                Code
                <i
                  class="bi"
                  [ngClass]="sortDirection === 'asc' ? 'bi-sort-numeric-down' : 'bi-sort-numeric-up-alt'"></i>
              </button>
            </th>
            <th>Material</th>
            <th>Category</th>
            <th>Company</th>
            <th>GST %</th>
            <th>HSN Code</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let material of rawMaterials">
            <td class="font-monospace">{{ material.code }}</td>
            <td>
              <div class="fw-semibold">
                <span class="text-muted font-monospace me-1">{{ material.category_short_code }}</span>
                &ndash;
                {{ material.name | titlecase }}
              </div>
            </td>
            <td>{{ material.category_name | titlecase }}</td>
            <td>
              @if (material.company_name) {
                {{ material.company_name }}
              } @else {
                <span class="text-muted">&mdash;</span>
              }
            </td>
            <td>{{ gstLabel(material.gst_percentage) }}</td>
            <td>
              @if (material.hsn_code) {
                <span class="font-monospace">{{ material.hsn_code }}</span>
              } @else {
                <span class="text-muted">&mdash;</span>
              }
            </td>
            <td>
              <div class="d-flex gap-2 justify-content-center">
                <button class="btn btn-action-edit" (click)="openEditModal(material)" title="Edit">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-action-delete" (click)="deleteRawMaterial(material.id)" title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    }
  </div>
}

<!-- 🔘 Create / Edit Raw Material Modal -->
<ng-template #rawMaterialModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <div class="modal-header">
      <h5 class="modal-title">
        {{ selectedRawMaterial?.id ? 'Update Raw Material' : 'Create Raw Material' }}
      </h5>
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
    </div>

    <form [formGroup]="rawMaterialForm" (ngSubmit)="submitRawMaterial(modal)">
      <div class="modal-body p-4">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Name</label>
            <input type="text" formControlName="name" class="form-control shadow-none" />
          </div>

          @if (selectedRawMaterial?.code) {
            <div class="col-md-6">
              <label class="form-label text-muted small">Code</label>
              <div class="form-control bg-light text-muted font-monospace">{{ selectedRawMaterial!.code }}</div>
            </div>
          }

          <div class="col-md-6">
            <label class="form-label">Category</label>
            <ng-select
              placeholder="Select Category"
              [items]="rawMaterialCategories"
              formControlName="category"
              bindValue="id"
              bindLabel="name"
              [clearable]="false"
              [searchable]="false" />
          </div>

          <div class="col-md-6">
            <label class="form-label">Unit</label>
            <ng-select
              placeholder="Select Unit"
              [items]="units"
              formControlName="unit"
              bindValue="id"
              bindLabel="name"
              [clearable]="false"
              [searchable]="false" />
          </div>

          <div class="col-md-6">
            <label class="form-label">Minimum Stock Level</label>
            <input type="number" formControlName="min_stock_level" class="form-control shadow-none" />
          </div>

          <div class="col-md-6">
            <label class="form-label">
              Company
              <span class="text-muted small fw-normal">(optional)</span>
            </label>
            <ng-select
              placeholder="Select Company"
              [items]="companies"
              formControlName="company"
              bindValue="id"
              bindLabel="name"
              [clearable]="true"
              [searchable]="true" />
          </div>

          <div class="col-md-6">
            <label class="form-label">
              HSN Code
              <span class="text-muted small fw-normal">(optional)</span>
            </label>
            <input
              type="text"
              formControlName="hsn_code"
              class="form-control shadow-none font-monospace"
              [class.is-invalid]="hsnCodeControl.invalid && hsnCodeControl.touched"
              placeholder="e.g. 3808"
              maxlength="15" />
            @if (hsnCodeControl.invalid && hsnCodeControl.touched) {
              <div class="text-danger small mt-1">HSN code can be at most 15 characters.</div>
            } @else {
              <div class="form-text text-muted">Partial codes are fine — fill it in later if unknown.</div>
            }
          </div>

          <div class="col-md-6">
            <label class="form-label">GST %</label>
            <div class="input-group">
              <input
                type="number"
                formControlName="gst_percentage"
                class="form-control shadow-none"
                [class.is-invalid]="gstPercentageControl.invalid && gstPercentageControl.touched"
                placeholder="0.00"
                step="0.01"
                min="0"
                max="100" />
              <span class="input-group-text bg-light text-muted">%</span>
            </div>
            @if (gstPercentageControl.invalid && gstPercentageControl.touched) {
              <div class="text-danger small mt-1">Enter a value between 0 and 100, with up to 2 decimals.</div>
            } @else {
              <div class="form-text text-muted">Default rate for this material. Leave blank or 0 for exempt goods.</div>
            }
          </div>
        </div>
      </div>

      <div class="modal-body pt-0 px-4 pb-0" *ngIf="submitError">
        <app-error-alert [error]="submitError"></app-error-alert>
      </div>

      <div class="modal-footer p-4">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="rawMaterialForm.invalid || isSubmitting">
          @if (isSubmitting) {
            <span class="spinner-border spinner-border-sm me-1"></span>
            Saving...
          } @else {
            {{ selectedRawMaterial?.id ? 'Update' : 'Create' }}
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>
